import { useCallback } from "react";

import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { ClassificationPrompt } from "components/File-repository/Table/ClassificationPrompt";
import { CurrencyPrompt } from "components/File-repository/Table/CurrencyPrompt";
import { EffectiveDatePrompt } from "components/File-repository/Table/EffectiveDatePrompt";
import { InvestorPrompt } from "components/File-repository/Table/InvestorPrompt";
import { StatusPrompt } from "components/File-repository/Table/StatusPrompt";
import { FileType } from "constants/index";
import { useModals, usePrompt } from "hooks";
import { useDealActions } from "store";
import { IFileMetaDetail, IFileRepo, IMetaDetails } from "store/types";

type Source = "deal" | "globalWorkspace";

interface UpdateFileFn {
  (fileId: number, patch: Partial<IFileRepo>): void;
}

interface BulkFileActionsConfig {
  /** Source context for data extraction */
  source: Source;
  /** Deal ID for investor prompt (null for global workspace) */
  dealId: number | null;
  /** Currently selected items */
  selectedItems: IMetaDetails[];
  /** Function to update file in local state */
  updateFile: UpdateFileFn;
  /** Ref to track last viewed file ID (for highlight after close) */
  lastViewedFileIdRef: React.MutableRefObject<number | null>;
  /** Trigger highlight animation */
  triggerHighlight: () => void;
  /** Show error messages (optional, defaults to true) */
  showErrors?: boolean;
}

export const useBulkFileActionsHook = ({
  source,
  dealId,
  selectedItems,
  updateFile,
  lastViewedFileIdRef,
  triggerHighlight,
  showErrors = true
}: BulkFileActionsConfig) => {
  const { bulkUpdateWorkspaceFilesAction } = useDealActions();
  const { errorModal } = useModals();
  const openPrompt = usePrompt();

  const filterPDFFiles = useCallback(() => {
    return selectedItems.filter(
      (item) => item.entity_type === "file" && item.type === FileType.PDF
    ) as IFileMetaDetail[];
  }, [selectedItems]);

  const filterFiles = useCallback(() => {
    return selectedItems.filter((item) => item.entity_type === "file") as IFileMetaDetail[];
  }, [selectedItems]);

  const showErrorIfNeeded = useCallback(
    (msg: string) => {
      if (showErrors) {
        errorModal({ msg });
      }
    },
    [showErrors, errorModal]
  );

  const handleBulkView = useCallback(async () => {
    const fileItems = filterPDFFiles();
    if (!fileItems.length) return;

    const fileIds = fileItems.map((item) => item.id);
    const fileNames = fileItems.reduce(
      (acc, item) => {
        acc[item.id] = item.name;
        return acc;
      },
      {} as Record<number, string>
    );

    lastViewedFileIdRef.current = fileIds[0];

    await openPrompt({
      componentType: "fullscreen",
      Component: DataExtractionModal,
      props: {
        fileIds,
        fileNames,
        initialShowParameters: false,
        source,
        onUpdateFile: (fileId: number, updates: Partial<IFileRepo>) => {
          updateFile(fileId, updates);
        },
        onCloseWithFileId: (currentFileId: number) => {
          lastViewedFileIdRef.current = currentFileId;
        }
      }
    });

    triggerHighlight();
  }, [filterPDFFiles, lastViewedFileIdRef, openPrompt, source, updateFile, triggerHighlight]);

  const handleBulkExtract = useCallback(async () => {
    const fileItems = filterPDFFiles();
    if (!fileItems.length) return;

    const fileIds = fileItems.map((item) => item.id);
    const fileNames = fileItems.reduce(
      (acc, item) => {
        acc[item.id] = item.name;
        return acc;
      },
      {} as Record<number, string>
    );

    lastViewedFileIdRef.current = fileIds[0];

    await openPrompt({
      componentType: "fullscreen",
      Component: DataExtractionModal,
      props: {
        fileIds,
        fileNames,
        source,
        onUpdateFile: (fileId: number, updates: Partial<IFileRepo>) => {
          updateFile(fileId, updates);
        },
        onCloseWithFileId: (currentFileId: number) => {
          lastViewedFileIdRef.current = currentFileId;
        }
      }
    });

    triggerHighlight();
  }, [filterPDFFiles, lastViewedFileIdRef, openPrompt, source, updateFile, triggerHighlight]);

  const handleChangeClassification = useCallback(
    async (anchorEl: HTMLElement | null) => {
      const fileItems = filterFiles();
      if (!fileItems.length) {
        showErrorIfNeeded("Please select at least one file.");
        return;
      }

      const result = await openPrompt({
        componentType: "popover",
        ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
        Component: ClassificationPrompt,
        props: {
          currentClassification: null
        }
      });

      if (!result) return;

      const updatedFiles = await bulkUpdateWorkspaceFilesAction({
        file_ids: fileItems.map((f) => f.id),
        document_type: result.classification
      });

      updatedFiles.forEach((updatedFile) => {
        updateFile(updatedFile.id, updatedFile as Partial<IFileRepo>);
      });
    },
    [filterFiles, showErrorIfNeeded, openPrompt, bulkUpdateWorkspaceFilesAction, updateFile]
  );

  const handleChangeCurrency = useCallback(
    async (anchorEl: HTMLElement | null) => {
      const fileItems = filterFiles();
      if (!fileItems.length) {
        showErrorIfNeeded("Please select at least one file.");
        return;
      }

      const result = await openPrompt({
        componentType: "popover",
        ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
        Component: CurrencyPrompt,
        props: {
          currentCurrency: null
        }
      });

      if (!result) return;

      const updatedFiles = await bulkUpdateWorkspaceFilesAction({
        file_ids: fileItems.map((f) => f.id),
        currency: result.currency
      });

      updatedFiles.forEach((updatedFile) => {
        updateFile(updatedFile.id, updatedFile as Partial<IFileRepo>);
      });
    },
    [filterFiles, showErrorIfNeeded, openPrompt, bulkUpdateWorkspaceFilesAction, updateFile]
  );

  const handleChangeInvestor = useCallback(
    async (anchorEl: HTMLElement | null) => {
      const fileItems = filterFiles();
      if (!fileItems.length) {
        showErrorIfNeeded("Please select at least one file.");
        return;
      }

      const result = await openPrompt({
        componentType: "popover",
        ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
        Component: InvestorPrompt,
        props: {
          currentInvestorId: null,
          currentAllInvestors: false,
          currentSplitByCommitment: false,
          dealId,
          showGrouped: false
        }
      });

      if (!result) return;

      const updatedFiles = await bulkUpdateWorkspaceFilesAction({
        file_ids: fileItems.map((f) => f.id),
        investor_id: result.investor?.id ?? null,
        split_by_commitment: result.splitByCommitment
      });

      updatedFiles.forEach((updatedFile) => {
        updateFile(updatedFile.id, updatedFile as Partial<IFileRepo>);
      });
    },
    [filterFiles, showErrorIfNeeded, openPrompt, dealId, bulkUpdateWorkspaceFilesAction, updateFile]
  );

  const handleChangeEffectiveDate = useCallback(
    async (anchorEl: HTMLElement | null) => {
      const fileItems = filterFiles();
      if (!fileItems.length) {
        showErrorIfNeeded("Please select at least one file.");
        return;
      }

      const result = await openPrompt({
        componentType: "popover",
        ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
        Component: EffectiveDatePrompt,
        props: {
          currentDate: null
        }
      });

      if (!result) return;

      const updatedFiles = await bulkUpdateWorkspaceFilesAction({
        file_ids: fileItems.map((f) => f.id),
        effective_date: result.effectiveDate
      });

      updatedFiles.forEach((updatedFile) => {
        updateFile(updatedFile.id, updatedFile as Partial<IFileRepo>);
      });
    },
    [filterFiles, showErrorIfNeeded, openPrompt, bulkUpdateWorkspaceFilesAction, updateFile]
  );

  const handleChangeStatus = useCallback(
    async (anchorEl: HTMLElement | null) => {
      const fileItems = filterFiles();
      if (!fileItems.length) {
        showErrorIfNeeded("Please select at least one file.");
        return;
      }

      const result = await openPrompt({
        componentType: "popover",
        ...(anchorEl ? { anchorEl } : { anchorPosition: { top: window.innerHeight / 2, left: window.innerWidth / 2 } }),
        Component: StatusPrompt,
        props: {
          currentStatus: "new"
        }
      });

      if (!result) return;

      const updatedFiles = await bulkUpdateWorkspaceFilesAction({
        file_ids: fileItems.map((f) => f.id),
        file_status: result.status
      });

      updatedFiles.forEach((updatedFile) => {
        updateFile(updatedFile.id, updatedFile as Partial<IFileRepo>);
      });
    },
    [filterFiles, showErrorIfNeeded, openPrompt, bulkUpdateWorkspaceFilesAction, updateFile]
  );

  return {
    handleBulkView,
    handleBulkExtract,
    handleChangeClassification,
    handleChangeCurrency,
    handleChangeInvestor,
    handleChangeEffectiveDate,
    handleChangeStatus
  };
};
